
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Instagram, Facebook, Youtube, Disc, Mail } from 'lucide-react';
import Logo from './Logo';

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Univers', href: '#about' },
    { label: 'Galerie', href: '#gallery' },
    { label: 'Musique', href: '#music' },
    { label: 'Avis', href: '#reviews' },
  ];

  const socialLinks = [
    { icon: <Instagram size={18} />, url: "https://www.instagram.com/djsaffmusic", label: "Instagram" },
    { icon: <Facebook size={18} />, url: "https://www.facebook.com/djsaffmusic", label: "Facebook" },
    { icon: <Youtube size={18} />, url: "https://www.youtube.com/@djsaffmusic", label: "YouTube" },
    { icon: <Disc size={18} />, url: "https://www.mixcloud.com/djsaffmusic/", label: "Mixcloud" },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? 'py-4 glass' : 'py-8 bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 flex justify-between items-center">
        <motion.a 
          href="#"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="transition-transform hover:scale-105"
        >
          <Logo className="w-28 md:w-32" />
        </motion.a>

        {/* Desktop Content */}
        <div className="hidden lg:flex items-center space-x-10">
          {/* Socials & Contact Icon */}
          <div className="flex items-center space-x-4 pr-6 border-r border-white/10">
            {socialLinks.map((social) => (
              <a 
                key={social.label}
                href={social.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/40 hover:text-gold transition-colors p-1"
                title={social.label}
              >
                {social.icon}
              </a>
            ))}
            <a 
              href="mailto:contact@djsaff.com"
              className="text-white/40 hover:text-gold transition-colors p-1"
              title="Contact par Email"
            >
              <Mail size={18} />
            </a>
          </div>

          <div className="flex items-center space-x-8">
            {navLinks.map((link, idx) => (
              <motion.a
                key={link.label}
                href={link.href}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="text-[10px] font-bold tracking-widest uppercase hover:text-gold transition-colors"
              >
                {link.label}
              </motion.a>
            ))}
            <motion.a
              href="#contact"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="px-6 py-2 bg-gold text-dark text-[10px] font-bold tracking-widest uppercase rounded-full hover:bg-white hover:text-dark transition-all duration-300 transform hover:scale-105"
            >
              Contacter DJ Saff
            </motion.a>
          </div>
        </div>

        {/* Mobile Content */}
        <div className="flex lg:hidden items-center space-x-4">
           <a 
              href="mailto:contact@djsaff.com"
              className="text-gold p-2 glass rounded-full"
            >
              <Mail size={20} />
            </a>
          <button 
            className="text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 bg-dark/98 backdrop-blur-xl flex flex-col items-center justify-center space-y-8 z-40 lg:hidden"
          >
            <Logo className="w-48 mb-8" />
            
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-3xl font-serif hover:text-gold transition-colors"
              >
                {link.label}
              </a>
            ))}

            <div className="flex space-x-6 pt-4">
              {socialLinks.map((social) => (
                <a 
                  key={social.label}
                  href={social.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-gold transition-colors"
                >
                  {social.icon}
                </a>
              ))}
            </div>

            <a
              href="#contact"
              onClick={() => setMobileMenuOpen(false)}
              className="px-8 py-3 bg-gold text-dark text-sm font-bold tracking-widest uppercase rounded-full"
            >
              Contacter DJ Saff
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
